import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { globalStores } from '@/editor/stores';
import { getWallSplitPoints } from '@/editor/stores/utils';
import { EEntityType } from '@/editor/types';
import { IShapeSeg2d } from '@/editor/types/shape';

type TWallType = EEntityType.Wall | EEntityType.SeparateWall;

/**
 * 按墙轮廓交点把墙中线修正为门窗实际可用的墙段。
 * rawSeg 保留原始墙段，用于计算 distance，避免后续门窗数据同步时出现位移。
 */
function correctSegBySplitPoints(
  wallId: string,
  wallType: TWallType,
  seg: IShapeSeg2d
): { seg: IShapeSeg2d; rawSeg?: LineSeg2d } {
  // 可继续优化，如果splitPoints[0], splitPoints[splitPoints.length - 1]超出墙体两端点，再选取其他点
  const splitPoints = getWallSplitPoints(wallId, wallType);
  if (splitPoints.length < 2) return { seg, rawSeg: undefined };

  return {
    seg: new LineSeg2d(splitPoints[0], splitPoints[splitPoints.length - 1]),
    rawSeg: seg instanceof ArcSeg2d ? undefined : seg
  };
}

function createWallSeg(wallData: { start: IPoint2d; end: IPoint2d; center?: IPoint2d; clockwise?: boolean }) {
  const { start, end, center, clockwise } = wallData;

  return center && clockwise !== undefined
    ? new ArcSeg2d({ start, end, center, clockwise })
    : new LineSeg2d(start, end);
}

/** 门窗所在墙的可用墙段，口径与吸附一致。墙体不存在时返回 null */
export function getWindowWallSeg(wallId: string, wallType: TWallType): IShapeSeg2d | null {
  if (wallType === EEntityType.SeparateWall) {
    const sepWallData = globalStores.separateWallStore.getSepData(wallId, false);
    if (!sepWallData) return null;

    return correctSegBySplitPoints(wallId, wallType, createWallSeg(sepWallData)).seg;
  }

  const wallData = globalStores.wallStore.getWallData(wallId);
  if (!wallData) return null;

  return correctSegBySplitPoints(wallId, wallType, createWallSeg(wallData.baseData)).seg;
}

export function getWindowAbsorbWallInfos() {
  const wallInfos = globalStores.wallStore.getWallDatas().map((wallData) => ({
    wallId: wallData.id,
    wallType: EEntityType.Wall as const,
    seg: createWallSeg(wallData.baseData)
  }));

  const separateWallInfos = globalStores.separateWallStore
    .getSepDatas({ onlyNotBase: true })
    .filter((item) => item.type !== ESeparateWallType.VirtualWall)
    .map((wallData) => ({
      wallId: wallData.id,
      wallType: EEntityType.SeparateWall as const,
      seg: createWallSeg(wallData)
    }));

  return [...wallInfos, ...separateWallInfos].map((wallInfo) => ({
    ...wallInfo,
    // 根据轮廓线进行修正
    ...correctSegBySplitPoints(wallInfo.wallId, wallInfo.wallType, wallInfo.seg)
  }));
}
